import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";

import DashboardLayout from "@/components/layout/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import Typography from "@/components/ui/typography";
import { useCredits } from "@/features/credits/hooks/useCredits";
import { useCurrentUser } from "@/hooks/useCurrentUser";

import ActiveSearchBanner from "../components/ActiveSearchBanner";
import CreditCounter from "../components/CreditCounter";
import IcpPrompt from "../components/IcpPrompt";
import InlineSearchFlow from "../components/InlineSearchFlow";
import LeadDetailDrawer from "../components/LeadDetailDrawer";
import LeadsTable from "../components/LeadsTable";
import LockedState from "../components/LockedState";
import RequestCreditsDialog from "../components/RequestCreditsDialog";
import { useSearchHistory, useUpdateLeadStatus } from "../hooks/useLeads";
import { Lead } from "../types/leads";
import { canAccessLeadFinder } from "../utils/access";

/** Only mounted for certified founders with ICP — keeps API hooks from firing for non-certified users. */
const CertifiedLeadsContent = ({ user }: { user: any }) => {
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [isCreditsDialogOpen, setIsCreditsDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);

  const { data: searchData } = useSearchHistory();
  const { creditsUsed, totalGranted: creditsTotal, balance: creditsRemaining, isLoading: creditsLoading } = useCredits();
  const updateLeadStatusMutation = useUpdateLeadStatus();

  const searches = searchData?.searches ?? [];
  const activeSearch = searches.find((s) => s.status === "running");
  const inProgressSearch = searches.find(
    (s) => s.status === "generating" || s.status === "previewing" || s.status === "preview_ready",
  );

  // Auto-open search flow only before the first completed search
  const hasCompletedSearch = searches.some((s) => s.status === "completed");
  useEffect(() => {
    if (!hasCompletedSearch && !isSearchActive && !activeSearch && !inProgressSearch && !creditsLoading) {
      setIsSearchActive(true);
    }
  }, [hasCompletedSearch, activeSearch, inProgressSearch, creditsLoading]);  

  const handleSearchComplete = () => {
    toast.success("New leads are ready!", {
      description: "Check the All Leads tab to see your new leads.",
    });
  };

  const handleUpdateLeadStatus = (leadId: string, status: Lead["status"]) => {
    updateLeadStatusMutation.mutate(
      { leadId, status },
      {
        onSuccess: (updatedLead) => {
          setSelectedLead((prev) =>
            prev?.id === updatedLead.id ? { ...prev, status: updatedLead.status } : prev,
          );
        },
      },
    );
  };

  return (
    <>
      {/* Header row */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Typography size="h2" className="text-[#1e1e24]">
            AI Lead Finder
          </Typography>
          <Badge variant="opaque" color="purple" className="text-[10px] uppercase tracking-wide">
            Beta
          </Badge>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
          {creditsLoading ? (
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-20 rounded" />
              <Skeleton className="h-1 w-12 rounded-full" />
            </div>
          ) : (
            <CreditCounter
              creditsUsed={creditsUsed}
              creditsTotal={creditsTotal}
              onRequestMore={() => setIsCreditsDialogOpen(true)}
            />
          )}
          <Button
            variant="default"
            size="sm"
            className="w-full sm:w-auto"
            onClick={() => setIsSearchActive(true)}
            disabled={creditsLoading || creditsRemaining <= 0 || !!activeSearch || isSearchActive || !!inProgressSearch}
          >
            New Search
          </Button>
        </div>
      </div>

      {/* Main content */}
      {activeSearch && (
        <ActiveSearchBanner
          search={activeSearch}
          onComplete={handleSearchComplete}
        />
      )}

      <InlineSearchFlow
        active={isSearchActive || !!inProgressSearch}
        onClose={() => setIsSearchActive(false)}
        resumeSearch={inProgressSearch}
        creditsRemaining={creditsRemaining}
      />

      <LeadsTable
        onSelectLead={setSelectedLead}
      />

      <LeadDetailDrawer
        lead={selectedLead}
        open={!!selectedLead}
        onOpenChange={(open) => !open && setSelectedLead(null)}
        searches={searches}
        onUpdateStatus={handleUpdateLeadStatus}
        isUpdatingStatus={updateLeadStatusMutation.isPending}
      />

      <RequestCreditsDialog
        open={isCreditsDialogOpen}
        onOpenChange={setIsCreditsDialogOpen}
        founderEmail={user?.email || ""}
        creditsUsed={creditsUsed}
        creditsTotal={creditsTotal}
      />
    </>
  );
};

const Leads = () => {
  const { user, isLoading } = useCurrentUser();

  // Wait for user data — avoid flashing any UI before we know if the user has access
  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="space-y-6" />
      </DashboardLayout>
    );
  }

  // Hard gate: if the user doesn't have access to the Lead Finder, redirect them away.
  // This prevents direct URL access, prevents cached JS from showing stale content, and
  // keeps the feature invisible to founders without the flag in production.
  if (!canAccessLeadFinder(user)) {
    return <Navigate to="/" replace />;
  }

  const level = user?.level?.toLowerCase();
  const isCertified =
    level === "certified" || level === "pro" || level === "x";
  const hasIcp = !!(user?.nicheWho && user?.nicheWhat);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {isCertified && hasIcp ? (
          <CertifiedLeadsContent user={user} />
        ) : (
          <>
            {/* Header for non-certified */}
            <div className="flex items-center gap-3">
              <Typography size="h2" className="text-[#1e1e24]">
                AI Lead Finder
              </Typography>
              <Badge variant="opaque" color="purple" className="text-[10px] uppercase tracking-wide">
                Beta
              </Badge>
            </div>

            {!isCertified ? <LockedState /> : <IcpPrompt />}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Leads;
